import { useCallback, useEffect, useState } from "react";
import { Save, Send, Trash2, CheckCircle2, AlertCircle, Info, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Admin CRUD + smoke-test UI for third-party integration credentials.
 * Currently supports Resend (transactional email). Extensible: drop in a
 * new <ServicePanel> when we add another integration.
 */
export default function IntegrationsAdmin() {
  const { user } = useAuth();
  if (user?.role !== "Owner") {
    return (
      <div data-testid="integrations-forbidden" className="p-6">
        <p className="text-sm text-muted-foreground">Only the Owner can manage integrations.</p>
      </div>
    );
  }
  return (
    <div data-testid="integrations-admin-page">
      <PageHeader
        eyebrow="Admin"
        title="Integrations"
        description="Third-party service credentials. Changes take effect immediately — no redeploy."
        testId="integrations-header"
      />
      <ResendPanel />
    </div>
  );
}

const INPUT_CLS = "w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";
const BTN_PRIMARY = "inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60";
const BTN_OUTLINE = "inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm font-medium hover:bg-secondary disabled:opacity-60";

// ─────────────────────────────────────────────────────────────
// Resend
// ─────────────────────────────────────────────────────────────
function ResendPanel() {
  const [existing, setExisting] = useState(null);   // {config, updated_*} or null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ api_key: "", sender_email: "", app_url: "" });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [flash, setFlash] = useState(null); // {kind, text}

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/integrations/resend");
      setExisting(data);
      // Never pre-fill api_key (masked on server anyway) — but seed sender/app_url
      setForm({
        api_key: "",
        sender_email: data.config?.sender_email || "",
        app_url: data.config?.app_url || "",
      });
    } catch (e) {
      if (e.response?.status === 404) {
        setExisting(null);
      } else {
        setError(formatApiError(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onSave = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    setFlash(null);
    try {
      // If existing config and user didn't type a new api_key, we still need
      // to send one — the server requires it. We can't recover the old one
      // (it's masked). Warn instead of silently sending blank.
      if (existing && !form.api_key.trim()) {
        setError("Enter the API key again (we can't read the stored one for security). Paste the current or new key.");
        setSaving(false);
        return;
      }
      await api.put("/admin/integrations/resend", {
        api_key: form.api_key.trim(),
        sender_email: form.sender_email.trim(),
        app_url: form.app_url.trim(),
      });
      setFlash({ kind: "ok", text: "Configuration saved." });
      setForm({ ...form, api_key: "" });
      reload();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!testTo.trim()) return;
    setTesting(true);
    setFlash(null);
    try {
      const { data } = await api.post("/admin/integrations/resend/test", { to: testTo.trim() });
      setFlash({ kind: "ok", text: `Test email sent to ${testTo}. ${data.message || ""}` });
      reload();
    } catch (err) {
      setFlash({ kind: "err", text: formatApiError(err.response?.data?.detail) || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove the Resend configuration? Emails will stop sending until you reconfigure.")) return;
    try {
      await api.delete("/admin/integrations/resend");
      setFlash({ kind: "ok", text: "Configuration removed." });
      reload();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="resend-panel">
      <div className="border-b border-border px-6 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Mail className="w-5 h-5" strokeWidth={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg">Resend — Transactional email</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Powers welcome emails, password resets, and forgot-email reminders.
          </p>
        </div>
        {existing && (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Configured
          </span>
        )}
      </div>

      <div className="p-6 space-y-5">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {existing && (
              <div className="text-xs text-muted-foreground bg-secondary/50 border border-border rounded px-3 py-2 space-y-0.5" data-testid="resend-current">
                <p>
                  <span className="label-tiny mr-2">Current key</span>
                  <span className="font-mono">{existing.config?.api_key_preview || "—"}</span>
                </p>
                <p>Updated {relTime(existing.updated_at)} by {existing.updated_by || "—"}</p>
                {existing.last_test && (
                  <p className={existing.last_test.ok ? "text-emerald-700" : "text-destructive"}>
                    Last test {relTime(existing.last_test.timestamp)}:
                    {" "}{existing.last_test.ok ? "✓" : "✗"} {existing.last_test.message}
                  </p>
                )}
              </div>
            )}

            <form onSubmit={onSave} className="space-y-4" data-testid="resend-form">
              <Field label="API key" hint="Starts with 're_'. Generate at resend.com → API Keys.">
                <input
                  type="password" autoComplete="new-password"
                  value={form.api_key}
                  onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  placeholder={existing ? "Paste to replace" : "re_..."}
                  className={INPUT_CLS} data-testid="resend-api-key"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Sender email" hint="Must be on a verified domain in Resend.">
                  <input
                    type="email" required
                    value={form.sender_email}
                    onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                    placeholder="noreply@yourdomain.com"
                    className={INPUT_CLS} data-testid="resend-sender-email"
                  />
                </Field>
                <Field label="App URL" hint="Used to build password links.">
                  <input
                    type="url" required
                    value={form.app_url}
                    onChange={(e) => setForm({ ...form, app_url: e.target.value })}
                    placeholder="https://roast.brotherscoffee.co.za"
                    className={INPUT_CLS} data-testid="resend-app-url"
                  />
                </Field>
              </div>
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="resend-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button type="submit" disabled={saving} className={BTN_PRIMARY} data-testid="resend-save-btn">
                  <Save className="w-4 h-4" /> {saving ? "Saving…" : (existing ? "Update" : "Save")}
                </button>
                {existing && (
                  <button type="button" onClick={onDelete} className={`${BTN_OUTLINE} text-destructive`} data-testid="resend-delete-btn">
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            </form>

            {existing && (
              <div className="border-t border-border pt-5" data-testid="resend-test-section">
                <p className="label-tiny mb-2">Test connection</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email" placeholder="Recipient for the test email"
                    value={testTo} onChange={(e) => setTestTo(e.target.value)}
                    className={INPUT_CLS} data-testid="resend-test-to"
                  />
                  <button onClick={onTest} disabled={testing || !testTo.trim()} className={BTN_OUTLINE} data-testid="resend-test-btn">
                    <Send className="w-4 h-4" /> {testing ? "Sending…" : "Send test"}
                  </button>
                </div>
                {flash && (
                  <div className={`mt-3 flex items-start gap-2 rounded-md border p-3 text-sm ${
                    flash.kind === "ok"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-destructive/30 bg-destructive/5 text-destructive"
                  }`} data-testid="resend-flash">
                    {flash.kind === "ok"
                      ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                      : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                    <span>{flash.text}</span>
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-border pt-5">
              <div className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="resend-help">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-foreground mb-1">Getting started</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Sign up at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">resend.com</a> and create an API key.</li>
                    <li>Paste the key + sender email + app URL above and click Save.</li>
                    <li>In Resend, verify your sending domain (SPF, DKIM, DMARC DNS records).</li>
                    <li>Click <em>Send test</em> to confirm delivery.</li>
                  </ol>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
